import { useState } from "react";
import { Loader2, Inbox } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Label } from "./ui/label";
import { fileUrl } from "../lib/api";
import { AVAIL, SITE_STATUS } from "../lib/format";
import { cn } from "../lib/utils";

export const PageHeader = ({ title, subtitle, actions, eyebrow }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6 fade-up">
    <div>
      {eyebrow && <div className="eyebrow mb-1.5">{eyebrow}</div>}
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Badge = ({ className, children, testid }) => (
  <span data-testid={testid} className={cn("inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold whitespace-nowrap", className)}>
    {children}
  </span>
);

export const AvailBadge = ({ status, testid }) => {
  const s = AVAIL[status] || AVAIL.disponibile;
  return (
    <Badge className={s.cls} testid={testid}>
      <span className={cn("h-2 w-2 rounded-full", s.dot)} />
      {s.label}
    </Badge>
  );
};

export const SiteStatusBadge = ({ status }) => {
  const s = SITE_STATUS[status] || SITE_STATUS.da_avviare;
  return <Badge className={s.cls}>{s.label}</Badge>;
};

export const Loading = ({ label = "Caricamento..." }) => (
  <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground" data-testid="loading">
    <Loader2 className="h-5 w-5 animate-spin" /> {label}
  </div>
);

export const Empty = ({ title = "Nessun dato", hint, icon: Icon = Inbox }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground" data-testid="empty-state">
    <Icon className="h-9 w-9 mb-3 opacity-60" />
    <div className="font-medium text-foreground/80">{title}</div>
    {hint && <div className="text-sm mt-1">{hint}</div>}
  </div>
);

export function DataState({ loading, empty, emptyTitle, children }) {
  if (loading) return <Loading />;
  if (empty) return <Empty title={emptyTitle} />;
  return children;
}

export const Kpi = ({ label, value, icon: Icon, accent = "bg-amber-500", hint, testid, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={cn("surface relative overflow-hidden p-4 text-left transition-colors hover:bg-accent/40 fade-up", !onClick && "cursor-default")}
  >
    <span className={cn("absolute left-0 top-0 h-full w-1", accent)} />
    <div className="flex items-start justify-between gap-2">
      <div className="eyebrow">{label}</div>
      {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
    </div>
    <div className="num text-[clamp(0.95rem,4vw,1.875rem)] sm:text-3xl font-semibold mt-2 whitespace-nowrap">{value}</div>
    {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
  </button>
);

export const Avatar = ({ name = "", photo, size = "h-10 w-10", className }) => {
  const [err, setErr] = useState(false);
  const initials = name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const src = fileUrl(photo);
  return src && !err ? (
    <img src={src} alt={name} onError={() => setErr(true)} className={cn(size, "rounded-lg object-cover border border-border", className)} />
  ) : (
    <div className={cn(size, "rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-300 flex items-center justify-center font-semibold text-sm font-heading", className)}>
      {initials}
    </div>
  );
};

export const Field = ({ label, children, className }) => (
  <div className={cn("space-y-1.5", className)}>
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export const InfoRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-border/60 last:border-0 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right font-medium break-all">{value || "—"}</span>
  </div>
);

export function ConfirmDialog({ open, onOpenChange, title = "Confermi l'eliminazione?", description, onConfirm, confirmLabel = "Elimina" }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-popover" data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description || "L'operazione non può essere annullata."}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel-btn">Annulla</AlertDialogCancel>
          <AlertDialogAction data-testid="confirm-ok-btn" className="bg-red-600 hover:bg-red-700 text-white" onClick={onConfirm}>
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export const selectCls =
  "h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

export const NativeSelect = ({ className, children, value, onChange, disabled, id, name, "data-testid": testId }) => (
  <select className={cn(selectCls, className)} value={value} onChange={onChange} disabled={disabled} id={id} name={name} data-testid={testId}>
    {children}
  </select>
);
